import React, { useState, useEffect, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';

const InstructionAutocomplete = ({ 
  value, 
  onChange, 
  options, 
  label, 
  onAddNew,
  placeholder = "Talimat ara...",
  type = "loading", // "loading" or "discharge"
  error = false,
  errorMessage = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);
  
  // Initialize the input value based on the selected value
  useEffect(() => {
    if (value) {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Filter options based on input value and type
  useEffect(() => {
    if (!options || !options.length) {
      setFilteredOptions([]);
      return;
    }

    // First filter by type
    const typeFiltered = options.filter(option => option.type === type);
    
    if (!inputValue) {
      setFilteredOptions(typeFiltered);
    } else {
      const filtered = typeFiltered.filter(option => 
        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.content.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options, type]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    onChange(option.content);
    setInputValue(option.content);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1 flex justify-between items-center">
        <span>
          {label} {error && <span className="text-red-500">*</span>}
        </span>
        <button
          type="button"
          onClick={onAddNew}
          className="text-blue-600 text-sm hover:text-blue-800 flex items-center"
        >
          <AddIcon className="w-4 h-4 mr-1" />
          Yeni Şablon Ekle
        </button>
      </label>
      <div className="relative">
        <textarea
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          autoComplete="new-password"
          rows={14}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => handleOptionClick(option)}
                className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
              >
                <div className="font-medium">{option.name}</div>
                <div className="text-sm text-gray-500 truncate">
                  {option.content.substring(0, 100)}{option.content.length > 100 ? '...' : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && errorMessage && (
        <p className="error-message text-red-500 text-xs mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default InstructionAutocomplete;
